import { createContext, ReactNode, useContext } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Icon from 'src/common/components/icons/Icon';
import TicketCard from 'src/common/components/styled/TicketCard';
import {
  ticketCardBadgeSx,
  ticketCardBadgesRowSx,
  ticketCardGridItemSx,
  ticketCardPublicGridPaddingSx,
} from 'src/common/styles/ticketCardStyles';
import { LanguageKeys } from 'src/common/constants/languages';
import { richTextParagraphSpacingSx } from 'src/helpers/richTextSpacing';
import { sortDistanceIds } from 'src/helpers/statisticExtras';
import { getTechnicalTypeDescription } from 'src/helpers/technicalTypeDescription';
import { isSignupOpenForEdition } from 'src/helpers/visibilityFilter';
import { isPublished } from 'src/helpers/publishedFilter';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

type CompetitionsListVariant = 'cards' | 'list';

/** When set (e.g. dashboard edition row), list that edition instead of the active one. */
const CompetitionsListEditionContext = createContext<string | undefined>(undefined);

export function CompetitionsListEditionProvider({
  editionId,
  children,
}: {
  editionId: string;
  children: ReactNode;
}) {
  return (
    <CompetitionsListEditionContext.Provider value={editionId}>
      {children}
    </CompetitionsListEditionContext.Provider>
  );
}

type CompetitionsListProps = {
  variant?: CompetitionsListVariant;
  /** When `variant` is `list`, matches parent ticket card contrast (e.g. active edition). */
  inverted?: boolean;
  /** Card grid columns on medium+ breakpoints; defaults to 4. */
  columnCount?: 2 | 4;
  /** Fixed 12-column grid spans for OG image capture (avoids breakpoint issues). */
  ogFixedLayout?: boolean;
  /**
   * When true, show competitions/distances attached to the edition even if unpublished
   * (admin historical views). Public signup surfaces should leave this false.
   */
  includeUnpublished?: boolean;
};

function cardGridSize(columnCount: 2 | 4, ogFixedLayout?: boolean) {
  if (ogFixedLayout) {
    return columnCount === 2 ? 6 : 3;
  }
  return columnCount === 2 ? { xs: 12, sm: 6, md: 6 } : { xs: 12, sm: 6, md: 3 };
}

export default function CompetitionsList({
  variant = 'cards',
  inverted = false,
  columnCount = 4,
  ogFixedLayout = false,
  includeUnpublished = false,
}: CompetitionsListProps) {
  const {
    i18n: { resolvedLanguage },
  } = useTranslation();
  const navigate = useNavigate();
  const currentLang = resolvedLanguage as LanguageKeys;
  const theme = useTheme();
  const { palette } = theme;

  const editionIdOverride = useContext(CompetitionsListEditionContext);
  const { activeEditionId, editionsById, competitionsById, distancesById } = useSystemOptionsStore(
    s => s,
  );

  const editionId = editionIdOverride ?? activeEditionId;
  const edition = editionId && editionsById ? editionsById[editionId] : undefined;
  const competitions = edition?.competitions ?? [];

  const distanceClicksEnabled = isSignupOpenForEdition(edition);

  if (!edition || !distancesById || !competitionsById || !competitions.length) {
    return null;
  }

  const publishedCompetitions = competitions.flatMap(({ competitionId, distances }) => {
    const competition = competitionsById[competitionId];
    if (!competition || (!includeUnpublished && !isPublished(competition))) return [];

    const distanceBadges = sortDistanceIds(distances, distancesById).flatMap(distanceId => {
      const distance = distancesById[distanceId];
      if (!distance || (!includeUnpublished && !isPublished(distance))) return [];
      const distanceName = distance.i18n[currentLang];
      return [
        {
          id: distanceId,
          label: distanceName,
          onClick: distanceClicksEnabled
            ? () =>
                navigate(`/race/signup?competition=${competition.id}&distance=${distance.id}`)
            : undefined,
        },
      ];
    });

    return [{ competition, competitionName: competition.i18n[currentLang], distanceBadges }];
  });

  if (!publishedCompetitions.length) {
    return null;
  }

  if (variant === 'list') {
    const nameColor = inverted ? 'primary.contrastText' : 'text.primary';
    const listIconColor = inverted ? 'primary.contrastText' : 'text.primary';

    return (
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {publishedCompetitions.map(({ competition, competitionName, distanceBadges }) => (
          <Box
            component="li"
            key={competition.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box
              aria-hidden
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                flexShrink: 0,
              }}
            >
              <Icon
                icon={competition.icon}
                sx={{
                  fontSize: 36,
                  width: 36,
                  height: 36,
                  color: listIconColor,
                }}
              />
            </Box>
            <Typography variant="body2" component="span" color={nameColor} sx={{ fontWeight: 500, minWidth: 0 }}>
              {competitionName}
            </Typography>
            {distanceBadges.length > 0 ? (
              <Box sx={{ ...ticketCardBadgesRowSx, mt: 0, pt: 0, flex: '1 1 auto', justifyContent: 'flex-end' }}>
                {distanceBadges.map(badge => (
                  <Chip
                    key={badge.id}
                    label={badge.label}
                    size="small"
                    clickable={Boolean(badge.onClick)}
                    onClick={badge.onClick}
                    sx={ticketCardBadgeSx(theme, false, inverted)}
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={richTextParagraphSpacingSx}>
      <Grid
        container
        spacing={2}
        sx={{
          overflow: 'visible',
          ...(ogFixedLayout ? { pl: 0, pr: 0, pb: 0 } : ticketCardPublicGridPaddingSx),
        }}
      >
        {publishedCompetitions.map(({ competition, competitionName, distanceBadges }) => (
          <Grid size={cardGridSize(columnCount, ogFixedLayout)} key={competition.id} sx={ticketCardGridItemSx}>
            <TicketCard
              solidPanel={ogFixedLayout}
              title={competitionName}
              description={getTechnicalTypeDescription(competition, currentLang)}
              avatar={
                <Avatar
                  sx={{
                    background: `linear-gradient(135deg, ${palette.success.dark} 0%, ${palette.success.light} 100%)`,
                  }}
                  aria-label={competitionName}
                >
                  <Icon fontSize="large" icon={competition.icon} />
                </Avatar>
              }
              badges={distanceBadges.map(({ label, onClick }) => ({
                label,
                onClick,
              }))}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
